import { ConfigTools } from '../view/ConfigTools.js';
import { WidgetRegistry } from './WidgetRegistry.js';

/** Разбивает строку с перечислением имён виджетов на массив */
function splitNames(s) {
    return s.trim().split(/[\s]+/).map(x => x.trim()).filter(Boolean);
}
/** Резолвит опции: propResolver → data-options → dataset */
function resolveOptions(def, el) {
    // если def ещё нет (ленивая загрузка), пропускаем кастомный парсер
    if (def?.propResolver) {
        try {
            return def.propResolver(el);
        }
        catch { }
    }
    // Парсинг опций
    return ConfigTools.parseDomOptions(el, def?.propScheme);
}
function processElement(el, processed, widgetsAll) {
    const mark = (name) => {
        const s = processed.get(el) ?? new Set();
        s.add(name);
        processed.set(el, s);
    };
    const seen = (name) => processed.get(el)?.has(name);
    const initNow = (def, name) => {
        if (!def?.ctor)
            return;
        def.ctor.getOrInit(el, resolveOptions(def, el));
        mark(name);
    };
    // 1) Обработка по атрибуту data-widget (если есть)
    if (el.hasAttribute('data-widget')) {
        const names = splitNames(el.getAttribute('data-widget') || '');
        for (const name of names) {
            if (!name || seen(name))
                continue;
            const def = WidgetRegistry.get(name);
            if (def?.ctor) {
                // конструктор уже загружен — инициализируем сразу
                try {
                    initNow(def, name);
                }
                catch (e) {
                    console.error(`[mountWidgets] data-widget ${name}`, e, el);
                }
            }
            else {
                // ленивка по имени
                mark(name); // пометить ДО загрузки, чтобы не продублировать
                WidgetRegistry.ensureLoaded(name)
                    .then(loadedDef => {
                    try {
                        initNow(loadedDef, name);
                    }
                    catch (e) {
                        console.error(`[mountWidgets] deferred init ${name}`, e, el);
                    }
                })
                    .catch(e => console.error(`[mountWidgets] ensureLoaded(${name})`, e));
            }
        }
    }
    // 2) Инициализация уже загруженных виджетов по cssKey (ctor есть)
    for (const def of widgetsAll) {
        if (!def.cssKey || !def.ctor)
            continue;
        if (!el.classList.contains(def.cssKey))
            continue;
        const name = def.name;
        if (seen(name))
            continue;
        try {
            initNow(def, name);
        }
        catch (e) {
            console.error(`[mountWidgets] cssKey ${name}`, e, el);
        }
    }
    // 3) Ленивая загрузка по классу: дергаем ensureLoaded ТОЛЬКО если для этого cssKey нет загруженного ctor
    //    (если уже есть загруженный деф с таким cssKey — пропускаем ensureLoaded)
    for (const css of Array.from(el.classList)) {
        // пропустим, если уже есть ЗАГРУЖЕННЫЙ виджет с таким cssKey
        if (widgetsAll.some(w => w.cssKey === css && !!w.ctor)) {
            // полезный диагностический лог, чтобы понять, почему ленивка не дергается
            continue;
        }
        WidgetRegistry.ensureLoaded(css)
            .then(def => {
            if (!def?.ctor)
                return;
            const name = def.name;
            if (seen(name))
                return;
            try {
                initNow(def, name);
            }
            catch (e) {
                console.error(`[mountWidgets] deferred init by css .${css}`, e, el);
            }
        })
            .catch(e => console.error(`[mountWidgets] ensureLoaded(.${css})`, e));
    }
}
function mountWidgets(root) {
    if (!root)
        return;
    const processed = new WeakMap();
    const widgetsAll = WidgetRegistry.all();
    // 0) сам root
    processElement(root, processed, widgetsAll);
    // 1) по data-widget
    root.querySelectorAll("[data-widget]")
        .forEach(el => processElement(el, processed, widgetsAll));
    // 1.5) по ленивым cssKey (ещё не загружены)
    const lazyCss = WidgetRegistry.listLazyCss();
    if (lazyCss.length) {
        const lazySelector = lazyCss.map(k => `.${k}:not([data-widget])`).join(",");
        if (lazySelector) {
            root.querySelectorAll(lazySelector)
                .forEach(el => {
                processElement(el, processed, widgetsAll);
            });
        }
    }
    // 2) по cssKey уже загружённых виджетов (как было)
    for (const def of widgetsAll) {
        if (!def.cssKey)
            continue;
        const selector = `.${def.cssKey}:not([data-widget])`;
        root.querySelectorAll(selector)
            .forEach(el => processElement(el, processed, widgetsAll));
    }
}
function mountWidgetsMany(nodes) {
    for (const el of nodes)
        mountWidgets(el);
}

export { mountWidgets, mountWidgetsMany, resolveOptions };
